import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  Users,
  FileText,
  Flag,
  ClipboardList,
  LogOut,
  Menu,
  X,
  UserCog,
  Megaphone,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/admin/hooks/useAdminAuth";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLE_LABELS } from "@/admin/types/admin";
import { useVerifications } from "@/admin/hooks/useVerifications";
import { useReports } from "@/admin/hooks/useReports";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, ownerOnly: false },
  { to: "/admin/verifications", label: "Vérifications", icon: ShieldCheck, ownerOnly: false },
  { to: "/admin/users", label: "Utilisateurs", icon: Users, ownerOnly: false },
  { to: "/admin/annonces", label: "Annonces", icon: FileText, ownerOnly: false },
  { to: "/admin/signalements", label: "Signalements", icon: Flag, ownerOnly: false },
  { to: "/admin/audit", label: "Audit Log", icon: ClipboardList, ownerOnly: false },
  { to: "/admin/banners", label: "Bandeaux", icon: Megaphone, ownerOnly: false },
  { to: "/admin/team", label: "Équipe", icon: UserCog, ownerOnly: true },
];

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { adminRole } = useAdminAuth();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const { query: verifQuery } = useVerifications("pending");
  const { query: reportsQuery } = useReports("open");

  const pendingVerifs = verifQuery.data?.length ?? 0;
  const openReports = reportsQuery.data?.length ?? 0;

  const badgeMap: Record<string, number> = {
    "/admin/verifications": pendingVerifs,
    "/admin/signalements": openReports,
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/connexion");
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
        <div>
          <p className="text-lg font-bold text-primary">Hirafi</p>
          <p className="text-xs text-muted-foreground">Espace Admin</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navItems
          .filter(({ ownerOnly }) => !ownerOnly || adminRole === "adminOwner")
          .map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-gray-700 hover:bg-gray-100"
              )
            }
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{label}</span>
            {badgeMap[to] > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                {badgeMap[to]}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer : rôle + déconnexion */}
      <div className="border-t border-gray-200 px-4 py-4 space-y-2">
        {adminRole && (
          <p className="text-xs text-center text-muted-foreground">
            <span className="font-medium text-gray-700">{ADMIN_ROLE_LABELS[adminRole]}</span>
          </p>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-gray-600 hover:text-red-600"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4" />
          Déconnexion
        </Button>
      </div>
    </div>
  );
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex lg:w-60 lg:flex-shrink-0 lg:flex-col border-r border-gray-200 bg-white">
        <SidebarContent />
      </aside>

      {/* Sidebar mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <aside className="relative z-50 flex h-full w-64 flex-col bg-white">
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Topbar mobile */}
        <header className="flex items-center gap-4 border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
          <p className="font-semibold text-primary">Admin Hirafi</p>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
